/**
 * Edit dialogs for a saved task. They only collect values; the caller's
 * onSave adds them to the local batch and nothing is persisted here.
 * Field state starts from props each time a dialog opens.
 */
import React, { useEffect, useId, useState } from 'react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';

const FIELD = 'w-full rounded border border-ink/30 bg-paper px-2 py-1 text-sm';

function DialogShell({ title, onCancel, onSubmit, children }) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40">
      <Card
        variant="lifted"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="p-6 w-[min(92vw,520px)] flex flex-col gap-4"
      >
        <h2 id={titleId} className="font-display text-xl font-semibold">{title}</h2>
        {children}
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSubmit}>Save</Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium">
      {label}
      {children}
    </label>
  );
}

function TaskForm({ name, description, onSave, onCancel }) {
  const [n, setN] = useState(name || '');
  const [d, setD] = useState(description || '');
  return (
    <DialogShell title="Edit task" onCancel={onCancel} onSubmit={() => onSave({ name: n, description: d })}>
      <Field label="Name">
        <input className={FIELD} value={n} onChange={(e) => setN(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea className={FIELD} rows={3} value={d} onChange={(e) => setD(e.target.value)} />
      </Field>
    </DialogShell>
  );
}

export function EditTaskDialog({ open, ...rest }) {
  return open ? <TaskForm {...rest} /> : null;
}

function InstructionsForm({ step, onSave, onCancel }) {
  const [prompt, setPrompt] = useState(step?.prompt || '');
  return (
    <DialogShell title="Edit instructions" onCancel={onCancel} onSubmit={() => onSave(prompt)}>
      <Field label="Instructions">
        <textarea className={FIELD} rows={8} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </Field>
    </DialogShell>
  );
}

export function EditInstructionsDialog({ open, ...rest }) {
  return open ? <InstructionsForm {...rest} /> : null;
}

function ModelForm({ step, onSave, onCancel }) {
  const [model, setModel] = useState(step?.model || '');
  const [tools, setTools] = useState((step?.tools || []).join(', '));
  const submit = () => onSave({
    model,
    tools: tools.split(',').map((t) => t.trim()).filter(Boolean),
  });
  return (
    <DialogShell title="Change model or tool" onCancel={onCancel} onSubmit={submit}>
      <Field label="Model">
        <input className={FIELD} value={model} onChange={(e) => setModel(e.target.value)} />
      </Field>
      <Field label="Tools">
        <input className={FIELD} value={tools} onChange={(e) => setTools(e.target.value)} />
      </Field>
    </DialogShell>
  );
}

export function ChangeModelDialog({ open, ...rest }) {
  return open ? <ModelForm {...rest} /> : null;
}
